import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import PhotoGallery from '../components/PhotoGallery';
import StatusManagement from '../components/StatusManagement';
import IPOriginTimeline from '../components/IPOriginTimeline';
import {
  ArrowLeft,
  X,
  UserCircle,
  Phone,
  Prohibit,
  Trash,
  Image as ImageIcon,
  Video,
  PencilSimple,
  Copy,
  Check
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect a video by its file extension so the gallery renders a video
// thumbnail (with a play badge) instead of a broken <img> ("corrupt media").
const isVideoUrl = (u) => /\.(mp4|webm|mov|m4v|avi|mkv|ogg|3gp)(\?|$)/i.test(u || '');

const HandlerProfile = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const [showEditLogin, setShowEditLogin] = useState(false);
  const [editLoginData, setEditLoginData] = useState({ phone: '', password: '' });
  const [copiedSection, setCopiedSection] = useState(null);
  const [settingsLoginLink, setSettingsLoginLink] = useState('');
  const [bankDetails, setBankDetails] = useState(null);
  const [clientBankDetails, setClientBankDetails] = useState(null);

  // P22 (admin/handler side) — open the customer profile/dashboard as a
  // centered DRAGGABLE + RESIZABLE modal (not full-screen), matching the
  // customer-side dashboard.
  const dragRef = useRef(null);
  const [box, setBox] = useState(() => {
    const w = Math.min(560, window.innerWidth - 24);
    const h = Math.min(Math.round(window.innerHeight * 0.88), window.innerHeight - 24);
    return { x: Math.max(12, (window.innerWidth - w) / 2), y: Math.max(12, (window.innerHeight - h) / 2), w, h };
  });
  function onDragMove(e) {
    const d = dragRef.current; if (!d) return;
    if (e.cancelable) e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - d.sx, dy = pt.clientY - d.sy;
    if (d.mode === 'drag') {
      setBox((b) => ({ ...b, x: Math.min(Math.max(0, d.ox + dx), window.innerWidth - 60), y: Math.min(Math.max(0, d.oy + dy), window.innerHeight - 40) }));
    } else {
      setBox((b) => ({ ...b, w: Math.max(320, Math.min(d.ow + dx, window.innerWidth - b.x - 8)), h: Math.max(340, Math.min(d.oh + dy, window.innerHeight - b.y - 8)) }));
    }
  }
  function onDragEnd() {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
    window.removeEventListener('touchmove', onDragMove);
    window.removeEventListener('touchend', onDragEnd);
  }
  function beginDrag(e, mode) {
    const pt = e.touches ? e.touches[0] : e;
    dragRef.current = { mode, sx: pt.clientX, sy: pt.clientY, ox: box.x, oy: box.y, ow: box.w, oh: box.h };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
  }
  const closeProfile = () => {
    const params = new URLSearchParams(window.location.search);
    const fromRoom = params.get('from_room');
    if (fromRoom) {
      const chatRoute = user.role === 'super_admin' ? `/admin-chat/${fromRoom}` : `/handler-chat/${fromRoom}`;
      navigate(chatRoute, { replace: true });
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(user.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard');
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const fromRoom = new URLSearchParams(window.location.search).get('from_room');
        const response = await axios.get(`${API}/customers/profile/${customerId}${fromRoom ? `?room_id=${fromRoom}` : ''}`);
        setProfile(response.data);
        // Fetch bank details from registered clients
        try {
          const regRes = await axios.get(`${API}/clients/registered`);
          const client = regRes.data?.find(c => c.id === customerId);
          if (client) setBankDetails(client);
        } catch (e) {}
        // Fetch client-saved bank details
        try {
          const clientBankRes = await axios.get(`${API}/client-bank-details/${customerId}`);
          if (clientBankRes.data?.bank_details) setClientBankDetails(clientBankRes.data.bank_details);
        } catch (e) {}
        setLoading(false);
      } catch (error) {
        console.error('Failed to load profile:', error);
        setLoading(false);
      }
    };

    loadProfile();
  }, [customerId]);

  // Fetch login link from settings
  useEffect(() => {
    const fetchLoginLink = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (res.data?.customer_login_link) {
          setSettingsLoginLink(res.data.customer_login_link);
        }
      } catch (e) { /* ignore */ }
    };
    fetchLoginLink();
  }, []);

  const handleBlock = async (mode = 'account') => {
    try {
      await axios.post(`${API}/customers/block/${customerId}`, {
        mode, // 'account' | 'device'
        actor_id: user.id,
        reason: `Blocked by ${user.role}`,
      });
      setShowBlockConfirm(false);
      toast.success(mode === 'device'
        ? 'Customer + device/IP blocked. They cannot register again from this device.'
        : 'Customer account blocked.');
      const dashboardRoute = user.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';
      navigate(dashboardRoute);
    } catch (error) {
      console.error('Failed to block customer:', error);
      toast.error(error?.response?.data?.detail?.message || 'Failed to block customer');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      setShowDeleteConfirm(false);
      alert('Customer deleted successfully');
      // Navigate based on user role
      const dashboardRoute = user.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';
      navigate(dashboardRoute);
    } catch (error) {
      console.error('Failed to delete customer:', error);
      alert('Failed to delete customer');
    }
  };

  // Edit Login: save updated credentials
  const handleSaveLogin = async () => {
    try {
      const updatePayload = {};
      if (editLoginData.phone) updatePayload.phone = editLoginData.phone;
      if (editLoginData.password) updatePayload.password = editLoginData.password;
      
      await axios.put(`${API}/clients/update-login/${customerId}`, updatePayload);
      toast.success('Login details updated!');
      setShowEditLogin(false);
      // Reload profile
      const response = await axios.get(`${API}/customers/profile/${customerId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to update login');
    }
  };

  // Reset to default password: Abcd1234
  const handleResetPassword = async () => {
    try {
      await axios.put(`${API}/clients/update-login/${customerId}`, {
        phone: profile?.customer?.phone || '',
        password: 'Abcd1234'
      });
      setEditLoginData(prev => ({ ...prev, password: 'Abcd1234' }));
      toast.success('Password reset to Abcd1234');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  // Copy login details
  const copyLoginDetails = async () => {
    const c = profile?.customer;
    if (!c) return;
    const loginLink = settingsLoginLink || `${window.location.origin}/customer-login`;
    const text = `Username.. ${c.username || 'N/A'}
DWS Login ID: ${c.phone || 'N/A'}
DWS Login Password: ${c.password || editLoginData.password || 'N/A'}
Login Link: ${loginLink}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection('login');
      toast.success('Login details copied!');
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Copy bank details
  const copyBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/clients/registered`);
      const client = response.data.find(c => c.id === customerId);
      if (!client) {
        toast.error('Client bank details not found');
        return;
      }
      let text = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;
      if (client.address) text += `\n\nAddress: ${client.address}`;
      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
      
      await navigator.clipboard.writeText(text);
      setCopiedSection('bank');
      toast.success('Bank details copied!');
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Failed to copy bank details');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center" data-testid="handler-profile-loading">
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    // Profile still loading from API — show a dim backdrop (instant load)
    return <div className="fixed inset-0 z-[60] bg-black/40" />;
  }

  const { customer, media, total_messages } = profile;
  const dashboardRoute = user.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';

  return (
    <div className="fixed inset-0 z-[60] bg-black/40" data-testid="handler-profile-backdrop">
      <div
        className="absolute bg-gray-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
        data-testid="handler-profile-modal"
      >
      {/* Header (drag handle) */}
      <div
        onMouseDown={(e) => beginDrag(e, 'drag')}
        onTouchStart={(e) => beginDrag(e, 'drag')}
        className="bg-[#25D366] text-white p-4 flex items-center gap-3 flex-shrink-0 cursor-move select-none"
      >
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={closeProfile}
          className="p-2 hover:bg-green-600 rounded-full"
          data-testid="handler-profile-back-btn"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold flex-1">Customer Profile</h1>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={closeProfile}
          className="p-2 hover:bg-green-600 rounded-full"
          data-testid="handler-profile-close-btn"
        >
          <X size={22} weight="bold" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Section */}
        <div className="bg-white p-6">
          <div className="flex items-start justify-between">
            {/* Left: Edit + Copy Login */}
            <div className="flex flex-col gap-2 pt-4">
              <button
                onClick={() => {
                  setEditLoginData({
                    phone: customer.phone || '',
                    password: ''
                  });
                  setShowEditLogin(true);
                }}
                className="p-2 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
                title="Edit Login"
                data-testid="edit-login-btn"
              >
                <PencilSimple size={20} className="text-blue-600" />
              </button>
              <button
                onClick={copyLoginDetails}
                className="p-2 bg-green-50 hover:bg-green-100 rounded-full transition-colors"
                title="Copy Login Details"
                data-testid="copy-login-btn"
              >
                {copiedSection === 'login' ? (
                  <Check size={20} className="text-green-600" />
                ) : (
                  <Copy size={20} className="text-green-600" />
                )}
              </button>
            </div>

            {/* Center: Profile */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden mb-4 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => customer.profile_pic && window.open(`${API}${customer.profile_pic}`, '_blank')}
                title={customer.profile_pic ? "Click to view full image" : "No profile picture"}
                style={customer.profile_pic ? {} : { backgroundColor: '#A2D7C6' }}
              >
                {customer.profile_pic ? (
                  <img 
                    src={`${API}${customer.profile_pic}`} 
                    alt={customer.username} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-bold" style={{color: '#0A6F67'}}>C</span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-800">{customer.username}</h2>
              {customer.client_id && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200"
                  title="Client ID (set by admin)"
                  data-testid="customer-client-id-badge"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">Client ID</span>
                  <span className="text-sm font-mono font-bold">{customer.client_id}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600 mt-2">
                <Phone size={16} />
                <span>{customer.phone}</span>
              </div>
              {customer.serial_number && (
                <span className="text-sm text-gray-400 mt-1">Code: {customer.serial_number}</span>
              )}
              {customer.created_at && (
                <span className="text-xs text-gray-400 mt-1">
                  Joined: {new Date(customer.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              {customer.same_ip_flag && (
                <span
                  className="mt-2 inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold"
                  title="Duplicate — this client shares a registration IP with other accounts. See the IP Origin Timeline below."
                >
                  <span className="w-3.5 h-3.5 rounded-sm bg-red-500 text-white flex items-center justify-center text-[9px] font-bold">D</span>
                  Duplicate IP
                </span>
              )}
              {customer.is_blocked && (
                <span className="mt-3 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                  Blocked
                </span>
              )}
            </div>

            {/* Right: Bank Copy */}
            <div className="flex flex-col gap-2 pt-4">
              <button
                onClick={copyBankDetails}
                className="p-2 bg-purple-50 hover:bg-purple-100 rounded-full transition-colors"
                title="Copy Bank Details"
                data-testid="copy-bank-btn"
              >
                {copiedSection === 'bank' ? (
                  <Check size={20} className="text-purple-600" />
                ) : (
                  <Copy size={20} className="text-purple-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white mt-2 p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Statistics</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">{total_messages}</p>
              <p className="text-sm text-gray-600">Messages</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{media.length}</p>
              <p className="text-sm text-gray-600">Media</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{profile.chat_rooms.length}</p>
              <p className="text-sm text-gray-600">Chats</p>
            </div>
          </div>
        </div>

        {/* IP Origin Timeline — only renders when the customer's IP has duplicates */}
        <IPOriginTimeline customerId={customer.id} currentCustomerName={customer.username} />

        {/* Media Gallery — WhatsApp-style horizontal strip (photos & videos only,
            scoped to this chat_type; stickers & voice excluded by the API) */}
        {media.length > 0 && (
          <div className="bg-white mt-2 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <ImageIcon size={24} />
              Media ({media.length})
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="profile-media-strip">
              {media.slice(0, 10).map((item, idx) => {
                const url = item.image_url?.startsWith('http') ? item.image_url : `${API}${item.image_url}`;
                const isVid = isVideoUrl(item.image_url);
                const showViewAll = idx === 9 && media.length > 10;
                return (
                  <div
                    key={idx}
                    className="relative flex-shrink-0 w-24 h-24 bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-all"
                    onClick={() => { setGalleryStartIndex(idx); setShowGallery(true); }}
                    data-testid={`profile-media-item-${idx}`}
                  >
                    {isVid ? (
                      <>
                        <video src={url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Video size={28} weight="fill" className="text-white drop-shadow" />
                        </div>
                      </>
                    ) : (
                      <img src={url} alt="Media" className="w-full h-full object-cover" />
                    )}
                    {showViewAll && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-semibold">
                        View all
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {media.length > 10 && (
              <button
                onClick={() => { setGalleryStartIndex(0); setShowGallery(true); }}
                className="text-sm text-blue-600 mt-2 hover:underline"
                data-testid="profile-media-view-all"
              >
                View all {media.length} media
              </button>
            )}
          </div>
        )}

        {/* Saved Bank Detail (System/Admin saved) */}
        <div className="bg-white mt-2 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#059669" viewBox="0 0 256 256"><path d="M24,104H232a8,8,0,0,0,4.19-14.81l-104-64a8,8,0,0,0-8.38,0l-104,64A8,8,0,0,0,24,104Zm104-62.43L207.72,88H48.28ZM208,144H192V120a8,8,0,0,0-16,0v24H160V120a8,8,0,0,0-16,0v24H112V120a8,8,0,0,0-16,0v24H80V120a8,8,0,0,0-16,0v24H48V120a8,8,0,0,0-16,0v24a16,16,0,0,0-8,13.84V184a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V157.84A16,16,0,0,0,224,144Z"/></svg>
              Saved Bank Detail
            </h3>
            <button onClick={copyBankDetails} className="p-1.5 hover:bg-gray-100 rounded-full" title="Copy bank details">
              <Copy size={18} className="text-gray-500" />
            </button>
          </div>
          {bankDetails && (bankDetails.bank_name || bankDetails.bank_account_number) ? (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              {bankDetails.bank_name && <div className="flex justify-between"><span className="text-gray-500">Bank</span><span className="font-medium text-gray-800">{bankDetails.bank_name}</span></div>}
              {(bankDetails.account_holder_name || bankDetails.client_name) && <div className="flex justify-between"><span className="text-gray-500">Holder</span><span className="font-medium text-gray-800">{bankDetails.account_holder_name || bankDetails.client_name}</span></div>}
              {bankDetails.bank_account_number && <div className="flex justify-between"><span className="text-gray-500">A/c No</span><span className="font-medium text-gray-800">{bankDetails.bank_account_number}</span></div>}
              {bankDetails.ifsc_code && <div className="flex justify-between"><span className="text-gray-500">IFSC</span><span className="font-medium text-gray-800">{bankDetails.ifsc_code}</span></div>}
              {bankDetails.upi_id && <div className="flex justify-between"><span className="text-gray-500">UPI</span><span className="font-medium text-gray-800">{bankDetails.upi_id}</span></div>}
              {bankDetails.address && <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium text-gray-800">{bankDetails.address}</span></div>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No saved bank details</p>
          )}
        </div>

        {/* Client Bank Detail (Customer-saved) */}
        <div className="bg-white mt-2 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#3b82f6" viewBox="0 0 256 256"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8C55.69,191.64,80.29,176,128,176s72.31,15.64,89.07,44a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"/></svg>
              Client Bank Detail
            </h3>
            {clientBankDetails && (clientBankDetails.bank_name || clientBankDetails.bank_account_number) && (
              <button onClick={async () => {
                let text = `User Name: ${profile?.username || 'N/A'}\n\nA/c Holder Name: ${clientBankDetails.account_holder_name || 'N/A'}\n\nBank Account No: ${clientBankDetails.bank_account_number || 'N/A'}\n\nIFSC Code: ${clientBankDetails.ifsc_code || 'N/A'}\n\nBank Name: ${clientBankDetails.bank_name || 'N/A'}`;
                if (clientBankDetails.address) text += `\n\nAddress: ${clientBankDetails.address}`;
                if (clientBankDetails.upi_id) text += `\n\nUPI ID: ${clientBankDetails.upi_id}`;
                navigator.clipboard.writeText(text);
                toast.success('Client bank details copied!');
              }} className="p-1.5 hover:bg-gray-100 rounded-full" title="Copy client bank details">
                <Copy size={18} className="text-gray-500" />
              </button>
            )}
          </div>
          {clientBankDetails && (clientBankDetails.bank_name || clientBankDetails.bank_account_number) ? (
            <div className="bg-blue-50 rounded-lg p-3 space-y-2 text-sm">
              {clientBankDetails.bank_name && <div className="flex justify-between"><span className="text-gray-500">Bank</span><span className="font-medium text-gray-800">{clientBankDetails.bank_name}</span></div>}
              {clientBankDetails.account_holder_name && <div className="flex justify-between"><span className="text-gray-500">Holder</span><span className="font-medium text-gray-800">{clientBankDetails.account_holder_name}</span></div>}
              {clientBankDetails.bank_account_number && <div className="flex justify-between"><span className="text-gray-500">A/c No</span><span className="font-medium text-gray-800">{clientBankDetails.bank_account_number}</span></div>}
              {clientBankDetails.ifsc_code && <div className="flex justify-between"><span className="text-gray-500">IFSC</span><span className="font-medium text-gray-800">{clientBankDetails.ifsc_code}</span></div>}
              {clientBankDetails.upi_id && <div className="flex justify-between"><span className="text-gray-500">UPI</span><span className="font-medium text-gray-800">{clientBankDetails.upi_id}</span></div>}
              {clientBankDetails.address && <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium text-gray-800">{clientBankDetails.address}</span></div>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Customer hasn't saved bank details yet</p>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white mt-2 p-4 space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Actions</h3>
          
          {!customer.is_blocked ? (
            <button
              onClick={() => setShowBlockConfirm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              <Prohibit size={20} weight="bold" />
              Block Customer
            </button>
          ) : (
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/customers/unblock/${customerId}`);
                  window.location.reload();
                } catch (error) {
                  alert('Failed to unblock');
                }
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              Unblock Customer
            </button>
          )}

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            <Trash size={20} weight="bold" />
            Delete Customer
          </button>
        </div>
      </div>

      {/* Block Confirmation Modal — 2 options: Account only OR Device+IP hard block */}
      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full" data-testid="block-customer-modal">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Block this customer?</h3>
            <p className="text-gray-600 text-sm mb-4">
              Choose the type of block. Device/IP block permanently prevents this browser + phone from registering any new account until admin unblocks.
            </p>
            <div className="space-y-2 mb-4">
              <button
                onClick={() => handleBlock('account')}
                data-testid="block-mode-account"
                className="w-full text-left px-4 py-3 border-2 border-orange-300 hover:bg-orange-50 rounded-lg"
              >
                <div className="font-bold text-orange-700">🚫 Block Customer Account</div>
                <div className="text-[11px] text-gray-600 mt-0.5">Only this specific account (serial number) is blocked. Same person can sign up again with a new phone.</div>
              </button>
              <button
                onClick={() => handleBlock('device')}
                data-testid="block-mode-device"
                className="w-full text-left px-4 py-3 border-2 border-red-500 bg-red-50 hover:bg-red-100 rounded-lg"
              >
                <div className="font-bold text-red-700">🚫 Block Customer + Device/IP</div>
                <div className="text-[11px] text-gray-600 mt-0.5">Fingerprint + every known IP blacklisted. This browser / phone cannot register OR login as anyone until admin unblocks.</div>
              </button>
            </div>
            <button
              onClick={() => setShowBlockConfirm(false)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              data-testid="block-modal-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Delete Customer?</h3>
            <p className="text-gray-600 mb-6">
              This will permanently delete the customer and all their data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {showGallery && (
        <PhotoGallery
          photos={media.filter(m => m.image_url).map(m => m.image_url.startsWith('http') ? m.image_url : `${API}${m.image_url}`)}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}

      {/* Edit Login Modal */}
      {showEditLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="bg-[#25D366] text-white p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="font-bold text-lg">Edit Login Details</h3>
              <button onClick={() => setShowEditLogin(false)} className="p-1 hover:bg-green-600 rounded-full">
                <ArrowLeft size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  value={customer.username || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DWS Login ID (Phone)</label>
                <input
                  type="text"
                  value={editLoginData.phone}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DWS Login Password</label>
                <input
                  type="text"
                  value={editLoginData.password}
                  onChange={(e) => setEditLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Login Link</label>
                <input
                  type="text"
                  value={settingsLoginLink || `${window.location.origin}/customer-login`}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleResetPassword}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold text-sm"
                >
                  Reset Default (Abcd1234)
                </button>
                <button
                  onClick={handleSaveLogin}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* P22 — resize handle (bottom-right corner) */}
      <div
        onMouseDown={(e) => { e.stopPropagation(); beginDrag(e, 'resize'); }}
        onTouchStart={(e) => { e.stopPropagation(); beginDrag(e, 'resize'); }}
        className="absolute w-6 h-6 cursor-se-resize z-10"
        style={{ left: box.x + box.w - 24, top: box.y + box.h - 24, background: 'linear-gradient(135deg, transparent 45%, #64748b 45%, #64748b 55%, transparent 55%, transparent 70%, #64748b 70%, #64748b 80%, transparent 80%)' }}
        data-testid="handler-profile-resize-handle"
      />
    </div>
  );
};

export default HandlerProfile;
